import { getPharosTokenClaims } from '../auth/get-pharos-token-claims';
import type { PendoIdentity } from './types';

/**
 * Decode a Klareo JWT into the minimal identity sent to Pendo.
 *
 * Pure function — no React dependency, safe to call from both web and React Native.
 * Only `userId`, the first role, and `tenantId` are ever included — no name, email, or
 * phone number reaches Pendo. Returns `null` when `token` is `null` or undecodable —
 * callers should treat `null` as "clear the Pendo session".
 *
 * @param token - Klareo JWT string, or `null` when the user is unauthenticated.
 * @returns The minimal Pendo identity, or `null` when no valid identity can be derived.
 */
export function toPendoIdentity(token: string | null): PendoIdentity | null {
  const claims = getPharosTokenClaims(token);
  if (!claims) return null;

  // isPharosJWTToken only validates `tenantId`/`userId` — guard the runtime shape of `roles`.
  const role = Array.isArray(claims.roles) ? claims.roles[0] : undefined;

  return {
    ...(role && { role }),
    // tenantId is guaranteed to be a string (never undefined) at this point, but an empty
    // string is still a falsy "no tenant" signal that must be omitted, not sent as ''.
    ...(claims.tenantId && { tenantId: claims.tenantId }),
    userId: claims.userId,
  };
}
